import React, {useState, useEffect, useRef, DragEvent} from 'react';
import {Button, Typography, Slider, TimePicker, Radio, message, Empty, type MenuProps, Dropdown} from 'antd';
import {
    UploadOutlined,
    ClockCircleOutlined,
    ToolOutlined,
    ThunderboltOutlined,
    DownOutlined
} from '@ant-design/icons';
import {FileUp} from "lucide-react";
import {useNotificationMessage} from "../../../hooks/NotificationContext";
import {coreUrl, wasmUrl} from "../../../constants";
import {FFmpeg} from "@ffmpeg/ffmpeg";
import {getCachedFile} from "../../../utils/filesUtil";
import {convertVideoToGIF, extractVideoSegment} from "./videoConvert";
import {useAlertMessage} from "../../../hooks/AlertContext";
import {hhmmssToSeconds} from "../../../utils";

const { Title, Text } = Typography;
const convertTypes = [
    {
        index: 1,
        text: "GIF",
        value: "gif",
        useResolution: true,// 使用分辨率
        useSpeed: true,// 使用播放速度
    },
    {
        index: 2,
        text: "MP4",
        value: "mp4",
        useResolution: false,
        useSpeed: false,
    }
];
const outputScaleSelectInfo = [// 输出分辨率
    {
        label: '240P【Community Emoticons】',
        key: 0,
        value: 240,
    },
    {
        label: '360P【Social Media Previews】',
        key: 1,
        value: 360,
    },
    {
        label: '450P【Blog Illustration】',
        key: 2,
        value: 450,
    },
    {
        label: '480P【Basic Video Tutorials】',
        key: 3,
        value: 480,
    },
    {
        label: '750P【E-commerce Platform】',
        key: 4,
        value: 750,
    },
    {
        label: '1080P【Maximum Resolution】',
        key: 5,
        value: 750,
    },
];
const modelSelectItems: MenuProps['items'] = outputScaleSelectInfo;

const ffmpeg = new FFmpeg();
export const VideoConvertor = () => {
    const {showNotification} = useNotificationMessage();
    const {showAlert} = useAlertMessage();

    const dropZoneRef = React.createRef();
    const videoRef = useRef<HTMLVideoElement>(null);

    const [curCoreBlobUrl, setCurCoreBlobUrl] = useState<string>('');
    const [curWasmBlobUrl, setCurWasmBlobUrl] = useState<string>('');

    const [outputScaleSelectKey, setOutputScaleSelectKey] = useState({
        label: outputScaleSelectInfo[0].label,
        value: outputScaleSelectInfo[0].value,
    });

    const [isDragging, setIsDragging] = useState(false);

    const [file, setFile] = useState<File | null>(null);
    const [videoUrl, setVideoUrl] = useState<string>("");
    const [outputFormatIndex, setOutputFormatIndex] = useState<number>(convertTypes[0].index);
    const [converting, setConverting] = useState<boolean>(false);
    const [fps, setFps] = useState(30);
    const [playbackRate, setPlaybackRate] = useState<number>(1);
    const [disabled, setDisabled] = useState<boolean>(true);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isFirstLoadVideo, setIsFirstLoadVideo] = useState(false);// 视频播放后第一次触发视频时间更新事件

    // State for start and end times (in seconds)
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(0);

    // State for input field values (in HH:mm:ss format)
    const [startInput, setStartInput] = useState('00:00:00');
    const [endInput, setEndInput] = useState('00:00:07');


    const loadFFMPEG = async () => {
        if (!ffmpeg.loaded) {
            const [coreBlob, wasmBlob] = await Promise.all([
                getCachedFile(coreUrl, "application/wasm"),
                getCachedFile(wasmUrl, "application/wasm")
            ]);
            setCurCoreBlobUrl(URL.createObjectURL(coreBlob));
            setCurWasmBlobUrl(URL.createObjectURL(wasmBlob));

            await ffmpeg.load({
                coreURL: curCoreBlobUrl,
                wasmURL: curWasmBlobUrl
            });
        }
    };

    useEffect(() => {
        if (curCoreBlobUrl) URL.revokeObjectURL(curWasmBlobUrl);
        if (curWasmBlobUrl) URL.revokeObjectURL(curWasmBlobUrl);

    }, []);

    // 处理视频URL
    useEffect(() => {
        if (file) {
            const url = URL.createObjectURL(file);
            setVideoUrl(url);
            setIsFirstLoadVideo(false);

            // 清理函数
            return () => {
                URL.revokeObjectURL(url);
            };
        } else {
            setVideoUrl("");
        }
    }, [file]);


    const handleTimeUpdate = async () => {
        if (!videoRef.current) return ;
        if (!isFirstLoadVideo) {
            setStartTime(0);
            setEndTime(videoRef.current.duration);
            setIsFirstLoadVideo(true);
        }

        const video = videoRef.current;
        // console.log(video.currentTime);
        // console.log(endTime);
        if (video.currentTime >= endTime) {
            video.currentTime = startTime;
            let url =  videoUrl;
            setVideoUrl("");
            setVideoUrl(url);
        }
    };

    // 文件上传处理
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (converting) {// 正在转换中...
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            });
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        if (e.target.files && e.target.files.length > 0) {
            const uploadedFile = e.target.files[0];
            if (uploadedFile.type.includes('video/')) {
                setFile(uploadedFile);
                setDisabled(false);
                showNotification("Video Upload Success", "success");
            } else {
                showNotification("Please Upload Video", "error");
            }
        }
    };

    // 拖放处理
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (converting) {// 正在转换中...
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            });
            return;
        }

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const uploadedFile = e.dataTransfer.files[0];
            if (uploadedFile.type.includes('video/')) {
                setFile(uploadedFile);
                setDisabled(false);
                showNotification("Video Upload Success", "success");
            } else {
                showNotification("Please Upload Video", "error");
            }
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    // 拖拽离开目标区域时隐藏蒙层
    const handleDragLeave = (e: DragEvent) => {
        const dropZone = dropZoneRef.current;
        if (dropZone) {
            const relatedTarget = e.relatedTarget;

            // 如果鼠标移入的是 dropZone 或其子元素，则不隐藏蒙层
            if ((dropZone as any).contains(relatedTarget)) return;
        }
        setIsDragging(false);
    };


    // Handle manual input in HH:mm:ss format
    const handleManualInput = (timeArray: [string, string]) => {
        const start = timeArray[0];
        const end = timeArray[1];
        setStartInput(start);
        setEndInput(end);

        // Validate and parse HH:mm:ss
        const startSeconds = hhmmssToSeconds(start);
        const endSeconds = hhmmssToSeconds(end);
        if (startSeconds <= endTime) {
            setStartTime(startSeconds);
            setEndTime(endSeconds);
        }
    };


    // 转换处理
    const handleConvert = async () => {
        if (converting) {// 正在转换中...
            showAlert({
                message: "A new task is running!!",
                type: "warning"
            });
            return;
        }
        if (!file || !videoUrl) {
            showNotification("Please Upload Video Before Converting", "warning");
            return;
        }

        setConverting(true);
        await loadFFMPEG();
        switch (outputFormatIndex) {
            case convertTypes[0].index:
                convertVideoToGIF(
                    ffmpeg,
                    videoUrl,
                    `GIF_${Date.now()}.gif`,
                    {
                        startTime: startInput,
                        endTime: endInput,
                        scale: outputScaleSelectKey.value,
                        fps: fps,
                        speed: playbackRate
                    }
                ).then(blob => {
                    if (!blob) return ;
                    // console.log(blob);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `GIF_${Date.now()}.gif`;
                    a.click();

                    setConverting(false);
                    showNotification(`Video Already Convert To ${convertTypes[outputFormatIndex - 1].text} Format`, "success");
                }).catch(error => {
                    console.error(error);

                    setConverting(false);
                    showNotification(`Fail To Convert Video`, "error");
                });
                break;
            case convertTypes[1].index:
                extractVideoSegment(
                    ffmpeg,
                    videoUrl,
                    `VIDEO_${Date.now()}.mp4`,
                    {
                        startTime: startInput,
                        endTime: endInput,
                    }
                ).then(blob => {
                    if (!blob) return ;
                    // console.log(blob);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `VIDEO_${Date.now()}.mp4`;
                    a.click();

                    setConverting(false);
                    showNotification(`Video Already Convert To ${convertTypes[outputFormatIndex - 1].text} Format`, "success");
                }).catch(error => {
                    console.error(error);

                    setConverting(false);
                    showNotification(`Fail To Convert Video`, "error");
                });
                break;
        }

    };

    // 清除当前视频
    const handleClearVideo = () => {
        setFile(null);
        setVideoUrl("");
        setDisabled(true);

        // 重置文件输入框的值
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // 渲染界面
    return (
        <div className="m-6 p-6 flex justify-center gap-[5%] flex-row w-[75vw] h-[700px] bg-white rounded-xl shadow-sm">
            <div className="w-[60%]">
                <div
                    className="flex flex-col h-[500px] items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 my-6 cursor-pointer transition-all hover:border-blue-500 hover:bg-blue-50 bg-gray-50 w-full h-[360px] relative"
                    // @ts-ignore
                    ref={dropZoneRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {videoUrl ? (
                        <>
                            <video
                                className="max-w-full max-h-full object-contain absolute top-0 left-0 w-full h-full bg-black rounded-xl"
                                src={videoUrl}
                                // @ts-ignore
                                ref={videoRef}
                                controls={false}
                                autoPlay={true}
                                loop={true}
                                muted
                                onTimeUpdate={handleTimeUpdate}
                            />
                            <div className="flex flex-col items-center justify-center w-full h-full z-10 bg-black/60 rounded-xl opacity-0 hover:opacity-100 transition-opacity absolute top-0 left-0">
                                <Text className="text-white mb-4">Click to replace video file</Text>
                                <div className="flex gap-3 mt-4">
                                    <Button
                                        type="primary"
                                        icon={<UploadOutlined />}
                                        className="vip-opt-button"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        Select Video
                                    </Button>
                                    <Button
                                        danger
                                        onClick={handleClearVideo}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* 灰色蒙层 */}
                            {isDragging && (
                                <div
                                    className="flex text-[#FF6F61] absolute top-0 left-0 w-[100%] h-[100%] z-[2050] bg-blue-50 bg-opacity-50 pointer-events-none justify-center items-center"
                                >Drop here</div>
                            )}
                            <FileUp className="mb-4 text-[#6366f1] h-[50px] w-[50px]"/>
                            <Text className="text-base text-gray-600 mb-4">Drag and drop video files here, or click
                                upload</Text>
                            <div className="flex gap-3 mt-4">
                            <Button
                                    type="primary"
                                    icon={<UploadOutlined />}
                                    className="vip-opt-button"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Select Video
                                </Button>
                            </div>
                        </>
                    )}

                    <input
                        type="file"
                        ref={fileInputRef}
                        id="fileInput"
                        onChange={handleFileUpload}
                        accept="video/*"
                        style={{ display: 'none' }}
                    />
                </div>
                <Button
                    type="primary"
                    onClick={handleConvert}
                    disabled={disabled}
                    loading={converting}
                    className="h-12 text-base vip-opt-button font-medium mt-6 w-[25%] rounded-[25px] bg-gradient-to-r from-blue-500 to-blue-700 border-none"
                >
                    {converting ? 'Converting...' : 'Convert it'}
                </Button>
            </div>

            <div className="relative bg-gray-50 w-[30%] rounded-lg p-6 mt-6">
                <Title level={5} className="mb-4">Output Setting</Title>

                <div className="flex items-center gap-2 mb-6">
                    <ClockCircleOutlined className="text-[gray]"/>
                    <Text strong>Time Frame</Text>
                </div>

                <div className="flex items-center gap-2 mb-6">
                    <TimePicker.RangePicker onChange={(e: any, timeArray: [string, string]) => {
                        // console.log(timeArray)
                        handleManualInput(timeArray);
                    }} />
                </div>

                <div className="flex items-center gap-2 mb-6">
                    <ToolOutlined className="text-[gray]" />
                    <Text strong>Output Format</Text>
                </div>

                <div className="flex justify-left mb-6">
                    {convertTypes.length && (
                        <Radio.Group
                            value={outputFormatIndex}
                            onChange={e => setOutputFormatIndex(e.target.value)}
                            className="mb-6"
                        >
                            {convertTypes.map((type) => (
                                <Radio.Button className="select-none" value={type.index}>{type.text}</Radio.Button>
                            ))}
                        </Radio.Group>
                    )}
                </div>

                {convertTypes[outputFormatIndex - 1].useResolution && (
                    <>
                        <div className="flex items-center gap-2 mb-6">
                            <ToolOutlined className="text-[gray]" />
                            <Text strong>Resolution</Text>
                        </div>
                        <div className="flex justify-left mb-6">
                            <Dropdown.Button
                                icon={<DownOutlined/>}
                                menu={{
                                    items: modelSelectItems,
                                    onClick: (e) => {
                                        setOutputScaleSelectKey({
                                            label: outputScaleSelectInfo[Number(e.key)].label,
                                            value: outputScaleSelectInfo[Number(e.key)].value,
                                        })
                                    },
                                }}
                            >
                                {outputScaleSelectKey.label}
                            </Dropdown.Button>
                        </div>
                    </>
                )}

                {convertTypes[outputFormatIndex - 1].useSpeed && (
                    <>
                        <div className="flex items-center gap-2 mb-6">
                            <ThunderboltOutlined className="text-[gray]" />
                            <Text strong>Playback Speed</Text>
                        </div>
                        <div className="mb-6">
                            <Slider
                                min={0.1}
                                max={10}
                                step={0.1}
                                value={playbackRate}
                                styles={{
                                    track: {
                                        backgroundColor: "#FF6F61",
                                    }
                                }}
                                onChange={(value) => setPlaybackRate(value as number)}
                                marks={{
                                    1: '1x',
                                    2: '2x',
                                    3: '3x',
                                    4: '4x',
                                    5: '5x',
                                    6: '6x',
                                    7: '7x',
                                    8: '8x',
                                    9: '9x',
                                    10: '10x',
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
